<template>
  <div class="view-product-register-module-lesson-basic">
    <snow-input
      v-model="form.name"
      :errors="errors.name"
      id="name"
      label="Nome da aula"
      :maxLength="240"
      required
    />
    <action-dispatcher
      action="membersModules/get"
      :parameters="{
        product_id: $route.params.id,
        limit: 1000
      }"
    >
      <snow-select
        v-model="form.module_id"
        :errors="errors.module_id"
        id="module_id"
        placeholder="Selecione"
        label="Módulo"
        :options="modulesOptions"
      />
    </action-dispatcher>
    <div>
      <snow-input
        v-model="form.link_media"
        :errors="errors.link_media"
        id="link_media"
        class="mb-1"
        label="Link da mídia"
      />
      <small class="text-dark-40 fs-14">
        Seus arquivos de video ou áudio precisam estar hospedados em um serviço,
        como YouTube, Vimeo, SoundCloud, etc. Atenção: no app mobile não haverá
        suporte de download de vídeos do YouTube e Vimeo, apenas vídeos HLS.
      </small>
    </div>

    <template v-if="isZoomLink">
      <div>
        <snow-input
          v-model="form.meeting_password"
          :errors="errors.meeting_password"
          id="meeting_password"
          class="mb-1"
          label="Senha da chamada do Zoom"
        />
        <small class="text-dark-40 fs-14">
          Insira a senha da sua chamada no zoom para que seus clientes não
          precisem digita-la ao entrar em sua aula ao vivo.
        </small>
      </div>
      <div>
        <snow-select
          v-model="form.meeting_view_type"
          :errors="errors.meeting_view_type"
          :options="meetingViewTypeOptions"
          id="meeting_view_type"
          class="mb-1"
          label="Tipo da aula ao vivo"
        />
        <small class="text-dark-40 fs-14">
          Escolha o tipo da sua aula ao vivo, controlando se seus alunos verão
          somente você na chamada do Zoom, ou se todos aparecerão em galeria.
        </small>
      </div>
    </template>

    <div class="form-group mb-0">
      <label>Texto de apoio (descrição)</label>
      <ckeditor
        :editor="editor"
        v-model="form.description"
        :config="ckeditorConfig"
      />
    </div>
    <tag-input
      v-model="form.tags"
      placeholder="Tags (separe as tags com vírgulas)"
      class="m-0"
    />
    <snow-switch
      id="comments_enabled"
      v-model="form.comments_enabled"
      label="Liberar comentários"
    />

    <div class="pt-4">
      <h5 class="text-semibold">
        Tempo da aula
      </h5>
      <h6 class="text-dark-40">
        Informe quanto tempo de duração tem a aula
      </h6>
    </div>

    <snow-input
      v-model="form.duration"
      :errors="errors.duration"
      mask="###########"
      info="Informe o tempo da aula em minutos. Exemplo: uma aula com 1h35 de duração,
      você deve inserir 95 minutos."
      id="duration"
      label="Minutos"
    />

    <div class="pt-4">
      <h5 class="text-semibold">
        Conteúdo complementar
      </h5>
      <h6 class="text-dark-40">
        Insira aqui materiais complementares para estudo.
      </h6>
    </div>

    <div>
      <dashed-add-resource
        icon="upload"
        title="Upload de arquivos"
        @onClick="openFilePicker('lesson-file-input')"
        :stars="false"
      >
        <input
          type="file"
          id="lesson-file-input"
          @change="onFileChange($event.target.files)"
          multiple
        />
      </dashed-add-resource>
      <div class="text-center" v-if="loadingFile">
        <b-spinner variant="primary" />
      </div>
      <div
        v-else
        class="file-wrapper d-flex align-items-center"
        :class="extra_files.length > 1 ? 'mb-4' : ''"
        v-for="(file, index) in extra_files"
        :key="index"
      >
        <div class="flex-fill">
          <i class="ph ph-file mr-4" />
          <span class="text-dark-40 fs-14">{{ file.name }}</span>
        </div>
        <snow-button
          variant="danger"
          text="Excluir"
          size="sm"
          @click="onRemoveFile(index)"
        />
      </div>
    </div>

    <div>
      <dashed-add-resource
        icon="link"
        title="Links externos"
        @onClick="visible.extra_reading = true"
        :stars="false"
      />
      <div
        v-for="(reading, index) in form.extra_reading"
        :key="index"
        :class="extra_files.length > 1 ? 'mb-4' : ''"
        class="complementary-reading"
      >
        <div class="d-flex justify-content-between">
          <div class="d-flex align-items-center gap-4">
            <h5 class="text-semibold">{{ reading.title }}</h5>
            <span class="text-dark-10">|</span>
            <h5 class="text-dark-40">
              {{ reading.site_name }}
            </h5>
          </div>
          <div class="d-flex gap-2">
            <snow-button
              size="sm"
              text="Editar"
              variant="gray"
              @click="onEditReading(reading, index)"
            />
            <snow-button
              size="sm"
              text="Excluir"
              variant="danger"
              @click="onRemoveReading(index)"
            />
          </div>
        </div>
        <h6>
          {{ reading.link }}
        </h6>
      </div>
    </div>

    <div class="pt-3">
      <h5 class="text-semibold">
        Liberação do conteúdo
      </h5>
      <h6 class="text-dark-40">
        Determine em quantos dias o seu conteúdo será liberado após a compra.
      </h6>
    </div>

    <snow-switch
      id="_days_to_release"
      label="Liberar após quantidade de dias"
      v-model="days_to_release"
    />

    <div v-if="days_to_release">
      <snow-input
        v-model="form.days_to_release"
        :errors="errors.days_to_release"
        id="days_to_release"
        label="Liberar após"
        mask="###"
      />
    </div>

    <snow-switch
      v-model="release_at"
      id="_release_at"
      label="Liberar em data específica"
    />

    <div v-if="release_at">
      <snow-date-picker
        v-model="form.release_at"
        :errors="errors.release_at"
        id="release_at"
        size="lg"
        isHours
        label="Liberar no dia"
      />
    </div>

    <responsive-modal v-model="visible.extra_reading" title="Link externo">
      <div class="d-flex flex-column gap-4">
        <snow-input
          v-model="extra_reading.link"
          id="link"
          label="Link do conteúdo"
        />
        <snow-input
          v-model="extra_reading.site_name"
          id="site_name"
          label="Nome do site"
        />
        <snow-input
          v-model="extra_reading.title"
          id="title"
          label="Título do artigo"
        />

        <div class="snow-submit-buttons mt-3">
          <snow-button
            size="lg"
            variant="gray"
            text="Cancelar"
            @click="visible.extra_reading = false"
          />
          <snow-button
            size="lg"
            :text="
              extra_reading.editedIndex !== undefined ? 'Salvar' : 'Adicionar'
            "
            @click="onSaveReadings"
          />
        </div>
      </div>
    </responsive-modal>
  </div>
</template>

<script>
import DashedAddResource from "@/components/product/details/content/step02/DashedAddResource.vue";
import { is_file_size_exceeds_limit_in_mb } from "@/functions/helpers";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  components: {
    DashedAddResource
  },
  props: {
    form: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    lesson: {
      type: Object,
      default: () => ({})
    },
    files: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const f = field => (get(this.lesson, field) || "").substr(0, 19);
    return {
      days_to_release: !!get(this.lesson, "days_to_release", 0),
      release_at: !!get(this.lesson, "release_at"),
      loadingFile: false,
      extra_files: this.form.extra_files || [],
      extra_reading: {
        title: "",
        site_name: "",
        link: "",
        editedIndex: undefined
      },
      visible: {
        extra_reading: false,
        file_upload: false
      },
      editor: ClassicEditor,
      ckeditorConfig: {
        toolbar: {
          items: [
            "undo",
            "redo",
            "|",
            "heading",
            "|",
            "bold",
            "italic",
            "|",
            "link",
            "blockQuote",
            "|",
            "bulletedList",
            "numberedList",
            "outdent",
            "indent"
          ],
          shouldNotGroupWhenFull: false
        },
        language: "pt-br"
      }
    };
  },
  computed: {
    ...mapState("membersModules", ["modules"]),
    tagsErrors() {
      return this.errors?.tags || [];
    },
    meetingViewTypeOptions() {
      return [
        {
          id: "speaker",
          name: "Aula ao vivo sem interação (Somente você aparece)"
        },
        {
          id: "gallery",
          name:
            "Aula ao vivo com interação (Você aparece juntamente com os alunos em galeria)"
        }
      ];
    },
    isZoomLink() {
      return !!`${this.form.link_media}`.match(".zoom.");
    },
    modulesOptions() {
      return this.modules.reduce(
        (prev, curr) => [
          ...prev,
          curr,
          ...curr.sub_modules.map(m => ({
            ...m,
            name: `${curr.name} > ${m.name}`
          }))
        ],
        []
      );
    }
  },
  methods: {
    openFilePicker(id) {
      document.getElementById(id).click();
    },
    onSaveReadings() {
      if (this.extra_reading.editedIndex !== undefined) {
        this.form.extra_reading.splice(
          this.extra_reading.editedIndex,
          1,
          this.extra_reading
        );
      } else {
        this.form.extra_reading = [
          ...this.form.extra_reading,
          this.extra_reading
        ];
      }
      this.extra_reading = {
        title: "",
        site_name: "",
        link: "",
        editedIndex: undefined
      };
      this.visible.extra_reading = false;
    },
    onEditReading(reading, editedIndex) {
      this.extra_reading = {
        ...reading,
        editedIndex
      };
      this.visible.extra_reading = true;
    },
    onRemoveReading(index) {
      this.form.extra_reading.splice(index, 1);
    },
    onFileChange(files) {
      if (files.length) {
        if (is_file_size_exceeds_limit_in_mb(files[0].size, 100)) {
          this.$message.error("O arquivo deve ter no máximo 100MB");
          return;
        }
        this.extra_files = [...this.extra_files, ...files];
      }
    },
    onRemoveFile(index) {
      this.extra_files = this.form.extra_files.filter((_, i) => i !== index);
    }
  },
  watch: {
    extra_files(files) {
      this.form.extra_files = files;
    },
    "form.extra_files"(files) {
      this.extra_files = files;
    },
    days_to_release(v) {
      if (!v) this.form.days_to_release = 0;
    },
    release_at(v) {
      if (!v) this.form.release_at = null;
    },
    "form.release_at": {
      immediate: true,
      handler(v) {
        const publishedAt = get(this.lesson, "published_at");
        this.release_at = !!v && v !== publishedAt;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.view-product-register-module-lesson-basic {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .complementary-reading {
    background: var(--bg-1);
    border-radius: 16px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .form-group {
    label {
      color: var(--dark) !important;
    }
  }
  ::v-deep .ck-editor {
    .ck-toolbar {
      border: 1px solid var(--dark-10) !important;
      border-radius: 8px 8px 0px 0px !important;
      background: var(--bg-2);
    }
    .ck-content {
      border: 1px solid var(--dark-10) !important;
      border-radius: 0px 0px 8px 8px !important;
    }
  }

  .file-wrapper {
    background: var(--bg-1);
    border-radius: 16px;
    padding: 16px;
  }

  ::v-deep.ck-content {
    height: 200px !important;
  }
}
</style>
